import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, DOCUMENT, inject, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Contact } from '@components/form/components/form-layout-3/model';
import { ContactUsService } from '@components/form/components/form-layout-3/services';
import { CacheService } from '@services/cache';
import { ElasticSearchService } from '@services/elastic-search';
import { LogsService } from '@services/logs';
import { ResponsiveService } from '@services/responsive';
import { ButtonComponent } from '@shared/components/button';
import { DropdownComponent } from '@shared/components/dropdown';
import { Dropdown } from '@shared/components/dropdown/model/dropdown';
import { FormItemComponent } from '@shared/components/form-item';
import { FormAbstractComponent } from '@shared/components/form-item/abstract';
import { FormItem, ValueType } from '@shared/components/form-item/model';
import { SearchComponent } from '@shared/components/search';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { CustomError } from '@shared/models';
import { debounceTime, EMPTY, Subject, switchMap, take, takeUntil } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';

interface DisplayBlock {
  storeResults: boolean;
  storeNotFound: boolean;
  storeLoading: boolean;
}

@Component({
  selector: 'app-form-layout-3',
  templateUrl: './form-layout-3.component.html',
  styleUrl: './form-layout-3.component.scss',
  imports: [TitleComponent, FormItemComponent, ButtonComponent, SearchComponent, DropdownComponent],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('0.2s ease-in', style({ opacity: 1 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormLayout3Component extends FormAbstractComponent implements OnDestroy {
  storeSuggestions = signal<Dropdown[]>([]);
  storeSelected = signal('');
  storeUuid = signal<string | undefined>(undefined);
  requestType = signal<'SAV' | 'COMMANDE' | 'RENSEIGNEMENT' | null>(null);
  isDisplay = signal<DisplayBlock>({
    storeResults: false,
    storeNotFound: false,
    storeLoading: false,
  });
  isSending = signal(false);
  isSendFailed = signal(false);
  dialogStoreIsOpened = signal(false);

  readonly title = signal<Title>({
    level: 'H1',
    text: 'Contactez-nous',
    color: 'WHITE',
    align: {
      mobile: 'LEFT',
      desktop: 'LEFT',
    },
    size: {
      mobile: 'H1',
      desktop: 'H1',
    },
  });

  specs = computed<FormItem[]>(() => [
    {
      type: 'TEXT',
      key: 'fullName',
      placeholder: 'saisir vos prénom et nom',
      autocomplete: 'off',
      rules: {
        regexp: /^([A-Za-zÀ-ÖØ-öø-ÿ]+[-\s]?)+\s+([A-Za-zÀ-ÖØ-öø-ÿ]+[-\s]?)+$/,
        tips: 'Une information est manquante',
      },
    },
    {
      type: 'SEARCH',
      key: 'requestType',
      placeholder: 'sélectionner un type de demande',
      list: [
        {
          key: 'RENSEIGNEMENT',
          label: 'Un renseignement',
        },
        {
          key: 'COMMANDE',
          label: 'Une commande en cours',
        },
        {
          key: 'SAV',
          label: 'Un SAV',
        },
      ],
    },
    {
      type: 'SEARCH',
      key: 'savCategory',
      placeholder: 'sélectionner un type de SAV',
      list: [
        {
          key: 'cuisine',
          label: 'une cuisine',
        },
        {
          key: 'salle_de_bain',
          label: 'une salle de bain',
        },
        {
          key: 'agencement',
          label: 'un agencement sur-mesure',
        },
        {
          key: 'electromenager',
          label: 'un électroménager',
        },
        {
          key: 'accessoire',
          label: 'un accessoire',
        },
        {
          key: 'espace_professionnel',
          label: 'un espace professionnel',
        },
        {
          key: 'autre',
          label: 'autre',
        },
      ],
    },
    {
      type: 'TEXT',
      key: 'postalCode',
      autocomplete: 'off',
      placeholder: 'saisir votre code postal',
      rules: {
        regexp: /^(\d{5}|\d{4}[A-Z]?|[0-9]{4,5})$/,
        tips: 'Code postal invalide',
      },
    },
    {
      type: 'TEXTAREA',
      key: 'message',
      placeholder: 'Votre message',
    },
    {
      type: 'TEXT',
      key: 'phone',
      autocomplete: 'off',
      placeholder: 'saisir votre numéro de téléphone',
      rules: {
        regexp: /^0[1-9]\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}$/,
        tips: 'Numéro de téléphone invalide',
      },
    },
    {
      type: 'TEXT',
      key: 'email',
      autocomplete: 'off',
      placeholder: 'saisir votre adresse email',
      rules: {
        regexp: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        tips: 'Adresse email invalide',
      },
    },
    {
      type: 'BOOLEAN',
      key: 'optin',
      label:
        "Je souhaite m'abonner à la newsletter Perene et recevoir chaque trimestre, de l'inspiration dans ma boîte mail.",
    },
  ]);

  readonly breakpoints = inject(ResponsiveService).breakpoints;
  readonly #document = inject<Document>(DOCUMENT);
  readonly #window = this.#document.defaultView as Window;
  readonly #router = inject(Router);
  readonly #elasticSearchService = inject(ElasticSearchService);
  readonly #contactUsService = inject(ContactUsService);
  readonly #cacheService = inject(CacheService);
  readonly #logsService = inject(LogsService);
  readonly #storeSearchInput: Subject<string> = new Subject();
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor() {
    super();
    this.#storeSearchInput
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        filter((value) => value.length > 1),
        tap(() => this.isDisplay.update((current) => ({ ...current, storeLoading: true }))),
        switchMap((value) => {
          return this.#elasticSearchService.getStoresByName(value, false).pipe(
            catchError((err: unknown) => {
              // do not log the typed search text itself (user input): length only
              this.#logsService.error(
                'FormLayout3Component',
                'Store search by name failed',
                { searchTermLength: value.length },
                err
              );
              this.isDisplay.update((current) => ({ ...current, storeLoading: false }));
              return EMPTY;
            })
          );
        })
      )
      .subscribe((stores) => {
        this.storeSuggestions.set(
          stores.map((store) => ({
            key: store.uuid,
            label: `${store.name}, ${store.address}`,
            name: store.name,
            address: store.address,
          }))
        );
        if (this.storeSuggestions().length > 0) {
          this.isDisplay.set({ storeResults: true, storeNotFound: false, storeLoading: false });
        } else {
          this.isDisplay.set({ storeResults: false, storeNotFound: true, storeLoading: false });
        }
      });
  }

  isStepVisible(requiredFields: string[]): boolean {
    for (const field of requiredFields) {
      if (!this.form[field]) {
        return false;
      }
    }
    return true;
  }

  shouldShowStore(): boolean {
    const type = this.requestType();
    return type === 'SAV' || type === 'COMMANDE';
  }

  shouldShowSavFields(): boolean {
    return this.requestType() === 'SAV';
  }

  onStoreNameInput(value: string): void {
    if (value.length <= 1) {
      this.storeSuggestions.set([]);
      this.isDisplay.set({ storeResults: false, storeNotFound: false, storeLoading: false });
      return;
    }
    this.#storeSearchInput.next(value);
  }

  onStoreSelected($event: Dropdown): void {
    this.storeSelected.set(String($event['name']));
    this.storeUuid.set(String($event['key']));
    this.updateForm({ key: 'store', value: String($event['key']), isValid: true });
  }

  onReset(): void {
    this.storeSuggestions.set([]);
    this.isDisplay.set({ storeResults: false, storeNotFound: false, storeLoading: false });
  }

  openStoreDialog(dialog: HTMLDialogElement): void {
    dialog.showModal();
    this.dialogStoreIsOpened.set(true);
    const searchInput: HTMLInputElement | null = this.#document.querySelector('.search-input > input');
    if (searchInput) {
      searchInput.focus();
    }
  }

  closeStoreDialog(location: HTMLDialogElement): void {
    location.close();
    this.dialogStoreIsOpened.set(false);
  }

  override updateForm(data: { key: string; value: ValueType; isValid: boolean }): void {
    super.updateForm(data);
    if (data.key === 'requestType' && data.isValid) {
      const typeValue = data.value as { key: string };
      this.requestType.set(typeValue.key as 'SAV' | 'COMMANDE' | 'RENSEIGNEMENT');
    }
  }

  onSubmit(): void {
    if (this.isSending()) return;
    if (this.isValidForm()) {
      this.isSending.set(true);
      this.isSendFailed.set(false);
      const form = this.form as Record<string, unknown>;
      const fullNameParts: string[] = (form['fullName'] as string).split(' ');

      const requestTypeObj = form['requestType'] as { key: string; label: string };
      const requestTypeKey = requestTypeObj.key;

      let toSend: Partial<Contact> = {
        firstname: fullNameParts[0]!,
        lastname: fullNameParts.slice(1).join(' '),
        phone: (form['phone'] as string).replace(/\s+/g, ''),
        email: form['email'] as string,
        message: (form['message'] as string) || '',
      };

      if (requestTypeKey === 'SAV') {
        const savCategoryObj = form['savCategory'] as { key: string; label: string };
        toSend.ssType = savCategoryObj.key;
        toSend.zipcode = form['postalCode'] as string;
        toSend.store = `/stores/${this.storeUuid()}`;
        toSend.type = '/contact_types/contact_sav';
      }

      if (requestTypeKey === 'COMMANDE') {
        toSend.ssType = 'order';
        toSend.store = `/stores/${this.storeUuid()}`;
        toSend.type = '/contact_types/contact_commande';
      }

      if (requestTypeKey === 'RENSEIGNEMENT') {
        toSend.ssType = 'information';
        toSend.type = '/contact_types/contact_renseignement';
        toSend.optin = !!form['optin'];
      }

      this.#logsService.debug('FormLayout3Component', 'Contact data to save:', {
        type: toSend.type,
        ssType: toSend.ssType,
        store: toSend.store,
        optin: toSend.optin,
      });

      this.#contactUsService
        .create(toSend as Contact, true)
        .pipe(
          take(1),
          tap(() => {
            // catch-then order makes the confirmation navigation unconditional
            void this.#cacheService
              .set('form-layout-3', {
                requestType: requestTypeKey,
                storeName: this.storeSelected(),
              })
              .catch((error: unknown) => {
                this.#logsService.error(
                  'FormLayout3Component',
                  'Failed to cache contact recap before confirmation',
                  error
                );
              })
              .then(() => {
                void this.#router.navigate([`${this.#window.location.pathname}/confirmation`]);
              });
          }),
          catchError((err: unknown) => {
            // err is the ResponseInterceptor wrapper { server, error, status } (CustomError).
            // Never log toSend (visitor PII); skip err.error too (validation bodies may echo fields).
            const { status, server } = err as Partial<CustomError>;
            this.#logsService.error('FormLayout3Component', 'Error while saving contact', {
              status,
              url: server?.url,
              type: toSend.type,
              ssType: toSend.ssType,
              store: toSend.store,
            });
            this.isSending.set(false);
            this.isSendFailed.set(true);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
